import { Form } from 'components/Wizards/Form/Form'
import { Formik, useFormikContext } from 'formik'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Button } from 'blaze-ui/Button'
import { ArrowRight, Plus } from 'blaze-ui/icons'
import Styles from './SubjectLineSelection.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useEffect, useState } from 'react'
import { RadioGroup } from 'components/RadioGroup/RadioGroup'
import { Radio } from 'components/RadioGroup'
import { useSubjectLineOptions } from './SubjectLineOptionsProvider'
import { Heading } from 'blaze-ui/Heading/Heading'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Text } from 'blaze-ui/Text/Text'
import { SubjectLineLoadingState } from './SubjectLineLoadingState'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { NewsletterIntroValues } from './Types'
import { useGenerateIntro } from './useGenerateIntro'
import { useSections } from '../SectionsSelection/SectionsProvider'
import { SectionValues } from '../SectionsSelection/Types'
import { useImages, useUnsplashImages } from 'components/Wizards/SelectImages/ImagesProvider'

interface Props extends StepProps {
  fieldsCompleted: boolean
  setFieldsCompleted: (val: boolean) => void
  onTemplateChange: (val: boolean) => void
}

export interface Values {
  selected_subject_line_id?: string
}

export const SubjectLineSelection = multiStep<Props>((props) => {
  const { schema, initialValues, onSubmit } = props

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      <Content {...props} />
    </Formik>
  )
})

const Content = (props: Props) => {
  const { schema, onBack } = props
  const {
    subjectLineOptions,
    abortGeneration,
    updateSubjectLineOption,
    updateSubjectLineOptions,
    generateSubjectLineOptions,
    isLoading,
  } = useSubjectLineOptions()
  const { generateSections, updateSections } = useSections()

  const { generateImageKeywords, topic, setMaxPhotos } = useImages()
  const { clearPhotos, setPage } = useUnsplashImages()
  const { isMobile } = useResponsiveDesign()
  const { values, setFieldValue, isSubmitting, submitForm } = useFormikContext()

  const generateIntro = useGenerateIntro({ onCancel: onBack })
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const fieldValues = values as Values

  useEffect(() => {
    if (!fieldValues.selected_subject_line_id) setFieldValue('selected_subject_line_id', subjectLineOptions[0]?.[0])
    if (subjectLineOptions.length === 0) setFieldValue('selected_subject_line_id', null)
  }, [fieldValues.selected_subject_line_id, setFieldValue, subjectLineOptions])

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields className={Styles.formFields}>
            <Heading level={5} id="wizard-header">
              Pick a subject line for your email:
            </Heading>
            {isLoading && !isLoadingMore && <SubjectLineLoadingState />}
            {(!isLoading || isLoadingMore) && (
              <>
                <RadioGroup
                  aria-label="Title"
                  value={fieldValues.selected_subject_line_id}
                  className={Styles.radioGroup}
                  onChange={(selectedUuid) => {
                    setFieldValue('selected_subject_line_id', selectedUuid)
                  }}
                >
                  {subjectLineOptions.map((subjectLineOption, i) => {
                    const [uuid, value] = subjectLineOption
                    return (
                      <Radio className={Styles.subjectLineOption} value={uuid} aria-label={value} key={uuid} hideRadio>
                        {(radioBulletProps) => (
                          <>
                            <div className={Styles.inputLabel}>
                              <Text variant="secondary" color={isMobile ? 'var(--dark-60)' : 'var(--dark-40)'}>
                                Subject Line #{i + 1}
                              </Text>
                            </div>
                            <div className={Styles.content}>
                              <div className={Styles.radioBullet}>
                                <RadioBullet {...radioBulletProps} size={20} />
                              </div>
                              <Input
                                isTextarea
                                scrollable={false}
                                size="lg"
                                name={`Subject Line ${i + 1}`}
                                value={value}
                                onChange={(e) => updateSubjectLineOption([uuid, e.target.value])}
                                autoFocus={value === ''}
                                aria-label="Generated Subject Line"
                                placeholder="Write your own subject line..."
                              />
                            </div>
                          </>
                        )}
                      </Radio>
                    )
                  })}
                </RadioGroup>
                {isLoadingMore && (
                  <div className={Styles.loadingMoreState}>
                    <SubjectLineLoadingState startPosition={subjectLineOptions.length + 1} />
                  </div>
                )}
                <div className={Styles.moreButton}>
                  <Button
                    variant="tertiary"
                    size="lg"
                    frontIcon={Plus}
                    onPress={() => {
                      setIsLoadingMore(true)
                      generateSubjectLineOptions(undefined, () => setIsLoadingMore(false))
                    }}
                    isDisabled={isLoading || isLoadingMore}
                  >
                    Generate 5 More
                  </Button>
                </div>
              </>
            )}
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>

      <Form.Actions isSticky>
        <Form.PreviousButton
          isDisabled={isSubmitting}
          onPress={() => {
            abortGeneration()
            updateSubjectLineOptions([])
            onBack?.()
          }}
        >
          {isMobile ? '' : 'Back'}
        </Form.PreviousButton>
        <Form.PrimaryActions>
          <Button
            size="lg"
            isDisabled={isSubmitting || !fieldValues.selected_subject_line_id}
            fullWidth={window.innerWidth < 450}
            endIcon={ArrowRight}
            onPress={() => {
              const selectedSubjectLine = subjectLineOptions.find(
                (s) => s[0] === fieldValues.selected_subject_line_id,
              )?.[1]
              generateIntro({
                ...fieldValues,
                generation_type: ChannelType.Newsletter,
                title: selectedSubjectLine,
              } as NewsletterIntroValues)

              updateSections([])
              generateSections({
                ...props.initialValues,
                title: selectedSubjectLine,
              } as SectionValues)

              setPage(1)
              setMaxPhotos(props.initialValues.count)

              if (topic !== selectedSubjectLine) {
                clearPhotos()
                generateImageKeywords({ topic: selectedSubjectLine })
              }

              submitForm()
            }}
          >
            {schema.describe().meta.submitLabel}
          </Button>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}
